import {
  useStringFlagValue,
  useNumberFlagValue,
  useObjectFlagValue,
  useBooleanFlagDetails,
  useStringFlagDetails,
  useNumberFlagDetails,
  useObjectFlagDetails,
} from '@openfeature/react-sdk';

import type { JsonValue } from '@openfeature/core';

import { updateFeatureFlagContext } from '@/config/datadog';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';

/**
 * Syncs OpenFeature evaluation context with auth state.
 * Mount once under UserProvider (see FeatureFlagContextUpdater in App.tsx).
 */
export const useUpdateFeatureFlagContext = () => {
  const isAuthenticated = useSelector(
    (state: WARootState) => state.auth.isAuthenticated,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const username = useSelector((state: WARootState) => state.auth.user);
  const userInfo = useSelector((state: WARootState) => state.appView.UserInfo);

  useEffect(() => {
    if (!isAuthenticated) {
      updateFeatureFlagContext({ targetingKey: 'anonymous' });
      return;
    }

    const userId = userInfo?.UserId;
    const targetingKey =
      userId != null && userId !== 0
        ? String(userId)
        : (username ?? 'anonymous');

    const advisorGroupId = deriveAdvisorGroupId(userInfo);

    updateFeatureFlagContext({
      targetingKey,
      user_id: userId != null && userId !== 0 ? String(userId) : undefined,
      user_role: currentRole || undefined,
      username: username ?? userInfo?.UserName ?? undefined,
      advisor_group_id: advisorGroupId ?? undefined,
    });
  }, [isAuthenticated, currentRole, username, userInfo]);
};

/**
 * Hook to get a boolean feature flag value
 * Evaluation exposure tracking is handled by the Datadog OpenFeature provider.
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 * @param suspend - Whether to suspend until provider is ready
 */
export const useFeatureFlag = (
  flagKey: string,
  defaultValue = false,
  suspend = true,
) => {
  const flagDetails = useBooleanFlagDetails(flagKey, defaultValue, { suspend });
  const flagValue = flagDetails.value;
  const flagVariant = flagDetails.variant;
  const flagReason = flagDetails.reason;

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    console.log(`Feature Flag "${flagKey}":`, {
      flagKey,
      value: flagValue,
      defaultValue,
      variant: flagVariant,
      reason: flagReason,
    });

    if (flagValue === defaultValue && flagReason !== 'TARGETING_MATCH') {
      console.warn(
        `⚠️ Flag "${flagKey}" returned default value. Reason: ${String(flagReason ?? 'unknown')}`,
      );
      console.warn(
        '  - Check that the flag key matches exactly (case-sensitive)',
      );
      console.warn(
        '  - Verify the flag is enabled in Datadog for this environment',
      );
      console.warn('  - Check evaluation context matches targeting rules');
    }
  }, [flagKey, flagValue, defaultValue, flagVariant, flagReason]);

  return flagValue;
};

/**
 * Hook to get a string feature flag value
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 * @param suspend - Whether to suspend until provider is ready
 */
export const useStringFeatureFlag = (
  flagKey: string,
  defaultValue = '',
  suspend = true,
) => {
  return useStringFlagValue(flagKey, defaultValue, { suspend });
};

/**
 * Hook to get a number feature flag value
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 * @param suspend - Whether to suspend until provider is ready
 */
export const useNumberFeatureFlag = (
  flagKey: string,
  defaultValue = 0,
  suspend = true,
) => {
  return useNumberFlagValue(flagKey, defaultValue, { suspend });
};

/**
 * Hook to get an object feature flag value
 * Note: Object evaluations are automatically tracked by DatadogProvider
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 * @param suspend - Whether to suspend until provider is ready
 */
export const useObjectFeatureFlag = <T extends Record<string, unknown>>(
  flagKey: string,
  defaultValue: T,
  suspend = true,
) => {
  // Type assertion needed due to OpenFeature SDK type constraints
  // The runtime behavior is correct, but types are stricter than necessary
  // intentional-any: OpenFeature SDK has strict JsonValue type constraints that don't match our use case
  return useObjectFlagValue(flagKey, defaultValue as JsonValue, {
    suspend,
  }) as unknown as T;
};

/**
 * Hook to get boolean feature flag details (value + metadata)
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 */
export const useFeatureFlagDetails = (
  flagKey: string,
  defaultValue = false,
) => {
  return useBooleanFlagDetails(flagKey, defaultValue);
};

/**
 * Hook to get string feature flag details (value + metadata)
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 */
export const useStringFeatureFlagDetails = (
  flagKey: string,
  defaultValue = '',
) => {
  return useStringFlagDetails(flagKey, defaultValue);
};

/**
 * Hook to get number feature flag details (value + metadata)
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 */
export const useNumberFeatureFlagDetails = (
  flagKey: string,
  defaultValue = 0,
) => {
  return useNumberFlagDetails(flagKey, defaultValue);
};

/**
 * Hook to get object feature flag details (value + metadata)
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag is not found
 */
export const useObjectFeatureFlagDetails = <T extends Record<string, unknown>>(
  flagKey: string,
  defaultValue: T,
  suspend = true,
) => {
  // Type assertion needed due to OpenFeature SDK type constraints
  // The runtime behavior is correct, but types are stricter than necessary
  // intentional-any: OpenFeature SDK has strict JsonValue type constraints that don't match our use case
  return useObjectFlagDetails(flagKey, defaultValue as JsonValue, {
    suspend,
  }) as unknown as {
    value: T;
    variant?: string;
    reason: string;
  };
};
